import lodashGet from "lodash/get";
import lodashForOwn from "lodash/forOwn";
import lodashHas from "lodash/has";

import {
  ENDPOINT_NEWS,
  ENDPOINT_NEWS_MAP,
  ENDPOINT_SENTIMENT,
} from "@/common/constants/api";
import { MEDIA_TYPES } from "@/common/constants/media";
import { removeHtmlTags, getUrlDomain } from "@/common/basic.utilities";
import { encodeAllURLParameters } from "@/common/api.utilities";
import { getPrecision } from "@/common/maps/utilities";
import { addMapData, removeMapData } from "@/common/maps/features";
import { httpGet } from "@/services/api.service";
import { parseStringToObject } from "@/common/basic.utilities";
import { findTopic } from "@/services/events/topics.service";

export { _getArticles as getArticles };
export { _addToMap as addToMap };
export { _removeFromMap as removeFromMap };
export { _formatFeatures as formatFeatures };
export { _formatFeature as formatFeature };
export { _getSentiment as getSentiment };

const _getArticles = async (params, type) => {
  let articles;
  try {
    let url;

    if (params.next) {
      url = params.next;
    } else {
      url = encodeAllURLParameters({
        baseEndpoint: ENDPOINT_NEWS,
        params: params,
      });
    }

    const RESULT = await httpGet({ url: url });
    const { data } = JSON.parse(RESULT);

    articles = data.features;

    if (!params.ignoreNext) {
      if (!params.articles) {
        params.articles = [];
      }
      articles = data.features.concat(params.articles);
    } else {
      articles = data.features;
    }

    if (!params.ignoreNext && data.metadata && data.metadata.next) {
      params.next = data.metadata.next;
      params.articles = articles;
      await _getArticles(params);
    }

    _formatFeatures(articles, type);

    return data;
  } catch (error) {
    Promise.reject(
      "Issue getting articles in news.service.js: " + JSON.stringify(error)
    );
  } finally {
    articles = null;
  }
};

const _addToMap = (params) => {
  const AGG_ENDPOINT = encodeAllURLParameters({
    baseEndpoint: ENDPOINT_NEWS_MAP,
    params: params,
    misc: "&precision=" + getPrecision(params.map),
  });
  const MARKER_ENDPOINT = encodeAllURLParameters({
    baseEndpoint: ENDPOINT_NEWS,
    params: params,
    misc: "&pageSize=10000",
  });

  addMapData({
    id: params.layerId,
    data: encodeURI(MARKER_ENDPOINT),
    aggData: encodeURI(AGG_ENDPOINT),
    map: params.map,
    mapClickEvent: params.mapClickEvent,
    clusterMapClickEvent: params.clusterMapClickEvent,
  });
};

const _removeFromMap = (params) => {
  removeMapData(params.map, params.layerId);
};

const _formatFeatures = (features, type) => {
  const LEN = features.length;
  for (let i = 0; i < LEN; i++) {
    let feature = features[i];
    _formatFeature(feature, type);
  }
};

const _formatFeature = (feature, params) => {
  if (feature.properties) {
    feature.properties.formatted = {};
    feature.properties.formatted.type = MEDIA_TYPES.news;
    feature.properties.formatted.title = "Oops, can't find a title";
    feature.properties.formatted.summary = "";
    feature.properties.formatted.language = null;
    feature.properties.formatted.adminInfo = "";

    feature.properties.formatted.esId = feature.properties._id
      ? feature.properties._id
      : null;
    feature.properties.formatted.clusterId = feature.properties.cluster_id
      ? feature.properties.cluster_id
      : null;
    feature.properties.formatted.clusterSize = feature.properties.cluster_size
      ? feature.properties.cluster_size
      : null;
    feature.properties.formatted.occurred = feature.properties["@timestamp"]
      ? feature.properties["@timestamp"]
      : null;

    feature.properties.formatted.clusterSize = null;
    if (feature.properties.cluster_size) {
      feature.properties.formatted.clusterSize =
        feature.properties.cluster_size;
      feature.properties.formatted.adminInfo += `Cluster size: ${feature.properties.cluster_size}<br>`;
    }

    feature.properties.formatted.location = null;
    if (
      lodashGet(feature, "geometry.coordinates") &&
      Array.isArray(feature.geometry.coordinates) &&
      feature.geometry.coordinates.length === 2
    ) {
      feature.properties.formatted.location = feature.geometry.coordinates;
    }

    if (lodashGet(feature, "properties.src")) {
      // NOTE (Nolan): Added this because some news-all records didn't have URL as part of their newspaper object but had it in src
      let src = feature.properties.src;
      if (typeof src === "string" || src instanceof String) {
        feature.properties = parseStringToObject(feature.properties, "src");
        src = feature.properties.src;
      }
      feature.properties.formatted.link = src.url;
      feature.properties.formatted.summary = src.url;
    }

    feature.properties.formatted.img = null;
    if (lodashGet(feature, "properties.src.links.sharing_image")) {
      if (
        Array.isArray(lodashGet(feature, "properties.src.links.sharing_image"))
      ) {
        feature.properties.formatted.img =
          feature.properties.src.links.sharing_image[0];
      } else {
        feature.properties.formatted.img =
          feature.properties.src.links.sharing_image;
      }
    }

    feature.properties.formatted.source = null;
    if (lodashGet(feature, "properties.common_src")) {
      feature.properties.formatted.source = feature.properties.common_src;
    } else {
      if (lodashGet(feature, "properties.src.url")) {
        feature.properties.formatted.source = getUrlDomain(
          feature.properties.src.url
        );
      }
    }

    feature.properties.formatted.translated = false;

    if (lodashGet(feature, "properties.newspaper")) {
      let newspaper = feature.properties.newspaper;
      if (typeof newspaper === "string" || newspaper instanceof String) {
        feature.properties = parseStringToObject(
          feature.properties,
          "newspaper"
        );
        newspaper = feature.properties.newspaper;
      }
      if (newspaper.id) {
        feature.properties.formatted.esId = newspaper.id;
      }
      //newspaper parent is id of original article
      if (newspaper.parent) {
        feature.properties.formatted.duplicate = newspaper.parent;
      }

      if (newspaper.title) {
        feature.properties.formatted.title = newspaper.title;
        if (newspaper.title_translated) {
          feature.properties.formatted.title = newspaper.title_translated;
        }
      }
      if (newspaper.summary) {
        feature.properties.formatted.summary = removeHtmlTags(
          newspaper.summary
        );
        if (newspaper.summary_translated) {
          feature.properties.formatted.summary = removeHtmlTags(
            newspaper.summary_translated
          );
        }
      }

      if (lodashHas(newspaper, "translated")) {
        feature.properties.formatted.translated = newspaper.translated;
      } else {
        feature.properties.formatted.translated = !!(
          newspaper &&
          newspaper.title_translated &&
          newspaper.summary_translated
        );
      }
      if (newspaper.lang) {
        feature.properties.formatted.language = newspaper.lang;
      }

      if (lodashHas(newspaper, "scores.tone")) {
        feature.properties.formatted.sentiment = newspaper.scores.tone;
      }
      feature.properties.formatted.sourceVeracity = null;
      if (lodashGet(newspaper, "veracity")) {
        feature.properties.formatted.sourceVeracity = newspaper.veracity;
      }
    }

    if (lodashGet(feature, "properties.topic_scores")) {
      let topicScores = feature.properties.topic_scores;
      if (typeof topicScores === "string" || topicScores instanceof String) {
        feature.properties = parseStringToObject(
          feature.properties,
          "topic_scores"
        );
      }
      topicScores = feature.properties.topic_scores;
      if (topicScores) {
        feature.properties.formatted.topic_scores =
          feature.properties.topic_scores;
        let scores = [];
        lodashForOwn(topicScores, (value, key) => {
          if (key && !isNaN(value)) {
            const TOPIC = findTopic(key, true, false);
            if (lodashGet(TOPIC, "scoreThreshold.cluster")) {
              scores.push(
                `<span class="application-heading">${key}</span>: ${(
                  value * 100
                ).toFixed(1)}% (${TOPIC.scoreThreshold.cluster * 100}%)`
              );
            }
          }
        });
        if (scores.length) {
          feature.properties.formatted.adminInfo += `${scores.join("<br/>")}`;
        }
      }
    }

    if (
      lodashGet(feature, "properties.location_metadata.method") &&
      feature.properties.location_metadata.method.toLowerCase() === "cliff"
    ) {
      let locParts = [];
      if (lodashGet(feature, "properties.location_metadata.city")) {
        locParts.push(feature.properties.location_metadata.city);
      }
      if (lodashGet(feature, "properties.location_metadata.state")) {
        locParts.push(feature.properties.location_metadata.state);
      }
      if (lodashGet(feature, "properties.location_metadata.country")) {
        locParts.push(feature.properties.location_metadata.country);
      }
      if (locParts.length) {
        feature.properties.formatted.locationName = locParts.join(", ");
      }
      locParts = null;
    }
  }
  if (params && params.noGeometry) {
    feature.geometry = null;
  }

  // remove extra properties
  delete feature.properties.newspaper;
  delete feature.properties.topic_scores;
  delete feature.properties.common_src;
  delete feature.properties.src;
  delete feature.properties.location_metadata;
};

const _getSentiment = async (params, type) => {
  try {
    const URL = encodeAllURLParameters({
      baseEndpoint: `${ENDPOINT_SENTIMENT}/news`,
      params: params,
    });

    const RESULT = await httpGet({ url: URL });
    const { data } = JSON.parse(RESULT);

    if (data) {
      return data;
    }
    return null;
  } catch (error) {
    Promise.reject(
      "Issue getting news sentiment in news.service.js: " +
        JSON.stringify(error)
    );
  }
};
